/**
 * AuthGate
 *
 * Wraps the whole app:
 * 1. Shows loading screen while session restores.
 * 2. Inside Telegram WebApp — auto-authenticates via initData (creates user
 *    if new, logs in if existing). No merge dialog — that is optional and
 *    lives in the profile settings page.
 * 3. Passes through to children once settled.
 */
import { useTranslation } from 'react-i18next'
import { useTelegramAuth } from '@/hooks/useTelegramAuth'
import { useAuth } from '@/context/AuthContext'

export default function AuthGate({ children }) {
  const { t } = useTranslation()
  const { isLoading: sessionLoading } = useAuth()
  const { isTelegram, status, error, retry } = useTelegramAuth()

  if (sessionLoading) return <LoadingScreen message={t('common.loading')} />
  if (isTelegram && status === 'loading') return <LoadingScreen message={t('auth.connecting', 'Ulanmoqda…')} />
  if (isTelegram && status === 'error') {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50 px-6">
        <div className="text-center max-w-xs space-y-4">
          <div style={{ fontSize: 48 }}>⚠️</div>
          <h2 className="font-bold text-gray-800">{t('common.error')}</h2>
          <p className="text-sm text-gray-500">{error ?? t('auth.loginFailed')}</p>
          <button onClick={retry} className="w-full py-3 bg-blue-600 text-white font-semibold rounded-2xl">
            {t('common.retry')}
          </button>
        </div>
      </div>
    )
  }

  return children
}

// ── Merge dialog ──────────────────────────────────────────────────────────────
function MergeAccountDialog({ onMerged, onSkip }) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState(null)

  async function handleMerge(e) {
    e.preventDefault()
    if (!username || !password) return
    setLoading(true); setError(null)
    try {
      const { data } = await mergeTelegramAccount(username, password)
      onMerged({ access: data.access, refresh: data.refresh, user: data.user })
    } catch (err) {
      setError(err?.response?.data?.detail || 'Xatolik yuz berdi')
    } finally {
      setLoading(false)
    }
  }

  const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: 12,
    border: '1.5px solid #e5e7eb', fontSize: 15, outline: 'none',
    background: '#f9fafb', color: '#111',
    boxSizing: 'border-box',
  }
  const btnStyle = (primary) => ({
    width: '100%', padding: '13px', borderRadius: 14, border: 'none',
    fontWeight: 700, fontSize: 15, cursor: 'pointer',
    background: primary ? '#f97316' : '#f3f4f6',
    color: primary ? '#fff' : '#374151',
    opacity: loading && primary ? 0.7 : 1,
  })

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: '100vh', padding: 24,
      background: '#fff',
    }}>
      <div style={{ maxWidth: 360, width: '100%' }}>
        {/* Icon */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 48, lineHeight: 1 }}>🔗</div>
          <h2 style={{ fontSize: 20, fontWeight: 800, margin: '12px 0 6px', color: '#111' }}>
            Hisobni ulash
          </h2>
          <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.5 }}>
            Siz avval ro'yxatdan o'tganmisiz?<br />
            Login va parol kiriting — Telegram akkauntingiz ulanadi.
          </p>
        </div>

        <form onSubmit={handleMerge} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input
            type="text"
            placeholder="Login (username)"
            value={username}
            onChange={e => setUsername(e.target.value)}
            autoCapitalize="none"
            autoCorrect="off"
            style={inputStyle}
          />
          <input
            type="password"
            placeholder="Parol"
            value={password}
            onChange={e => setPassword(e.target.value)}
            style={inputStyle}
          />

          {error && (
            <p style={{ fontSize: 13, color: '#ef4444', textAlign: 'center', margin: 0 }}>
              {error}
            </p>
          )}

          <button type="submit" disabled={loading} style={btnStyle(true)}>
            {loading ? 'Tekshirilmoqda…' : 'Ulash'}
          </button>
        </form>

        <div style={{ margin: '16px 0', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
          yoki
        </div>

        <button onClick={onSkip} style={btnStyle(false)}>
          Yo'q, yangi hisob bilan davom etish
        </button>
      </div>
    </div>
  )
}

function LoadingScreen({ message }) {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-4 bg-blue-700">
      <div className="text-white text-3xl font-black tracking-tight">5mi.uz</div>
      <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
      <p className="text-blue-200 text-sm">{message}</p>
    </div>
  )
}
